/**
 * The four dashboards — `dashboard-{rep,manager,exec,admin}.html`.
 *
 * One file because they are four arrangements of the same parts: a hero band or
 * an alert strip, a KPI row, charts, and working lists. Splitting them would
 * duplicate the tile row four times to save nothing.
 *
 * The layout is the prototype's, down to the grid: `row g-3` with
 * `col-6 col-lg-3` for the KPI band and `col-xl-8` / `col-xl-4` for the body,
 * because that is what `app.css` was written against.
 *
 * **What is deliberately not reproduced.** The prototype's headers carry
 * segmented Week/Quarter/Year toggles, territory selects, Export, Board pack and
 * Share buttons. None of them do anything there, and none of them have an
 * endpoint here — `docs/API.md` §12 dashboards is unbuilt. A row of controls
 * that silently ignore the click is worse than a header without them, so the
 * headers keep only actions that navigate somewhere real. The same rule governs
 * the approve/reject buttons in the manager's queue: they link to the quotation
 * screen that owns the decision rather than pretending to take it here.
 *
 * Which dashboard a user lands on comes from `landing_screen` on `/auth/me` —
 * the highest-priority role they hold — not from a preference. A rep covering as
 * a manager gets the manager view for the duration of the grant, which is the
 * behaviour the permission model already implies.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  getDashboard, listAuditTrail, listOccasions,
} from '../../api/crm.api.js';
import { listFollowUps } from '../../api/followups.api.js';
import { listActivities } from '../../api/activities.api.js';
import { KIND_META, KIND_LABEL, relatedRecordLinks } from '../activities/ActivityTimeline.jsx';
import { feedVerb } from '../activities/feedFormat.jsx';
import { queryKeys } from '../../app/queryClient.js';
import { isMock } from '../../api/mockFallback.js';
import { useAuth } from '../../hooks/useAuth.js';
import { BarChart, DonutChart, ComboChart } from '../../components/charts/index.jsx';
import {
  PageHeader, Card, StatTile, Badge, Avatar, Meter, ErrorState, MockBadge, EmptyState,
  SkeletonDashboard, SkeletonTable, SkeletonText,
} from '../../components/common/index.jsx';
import {
  money, moneyShort, number, percent, relativeTime, dueLabel,
} from '../../utils/formatters.js';
import {
  greeting, TodayActivitiesCard, PipelineFlowCard, MySalesCard, UpcomingTodayCard,
  OverdueActivitiesCard, NewLeadsCard, MyOpportunitiesCard, RecentQuotationsCard,
  PendingApprovalsCard, RecentCustomersCard, DailySummaryCard,
} from './RepDashboardWidgets.jsx';

/* -------------------------------------------------------------------------- */
/* KPI band                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The icon each KPI wears.
 *
 * Keyed on `kpi.key`, so adding a KPI server-side still renders — it just gets
 * the generic icon until someone chooses one. A dashboard where all four tiles
 * carry the same glyph is a row of numbers with decoration, not a scannable
 * board.
 */
const KPI_ICON = {
  quota: 'bi-bullseye',
  teamQuota: 'bi-bullseye',
  leads: 'bi-magnet-fill',
  pipeline: 'bi-funnel-fill',
  won: 'bi-trophy-fill',
  followups: 'bi-arrow-repeat',
  activities: 'bi-list-check',
  approvals: 'bi-check2-square',
  slipping: 'bi-hourglass-split',
  arr: 'bi-cash-stack',
  forecast: 'bi-graph-up-arrow',
  winRate: 'bi-percent',
  customers: 'bi-people-fill',
  cycle: 'bi-speedometer2',
  users: 'bi-people-fill',
  records: 'bi-database-fill',
  sessions: 'bi-broadcast',
  failed: 'bi-shield-exclamation',
  integrations: 'bi-plugin',
  jobs: 'bi-hdd-stack-fill',
};

/**
 * KPIs where a fall is the good news. Colouring those red would read as an
 * alarm about an improvement.
 */
const INVERTED = new Set(['cycle', 'slipping', 'failed', 'followups', 'overdue', 'jobs']);

/** A KPI value may already be a display string ("2.8M"); leave those alone. */
function kpiValue(kpi) {
  if (typeof kpi.value === 'string') return kpi.value;
  if (kpi.format === 'money') return moneyShort(kpi.value);
  if (kpi.format === 'percent') return percent(kpi.value);
  return number(kpi.value);
}

function KpiRow({ kpis = [], cols = 'col-6 col-lg-3', className = 'row g-3 mb-3' }) {
  return (
    <div className={className}>
      {kpis.map((kpi, i) => (
        <div className={cols} key={kpi.key}>
          <StatTile
            className={`h-100 fade-up d-${i + 1}`}
            label={kpi.label}
            value={kpiValue(kpi)}
            suffix={kpi.suffix}
            delta={kpi.delta}
            deltaSuffix={kpi.deltaSuffix}
            invertDelta={INVERTED.has(kpi.key)}
            tone={kpi.tone}
            icon={KPI_ICON[kpi.key] ?? 'bi-graph-up-arrow'}
            meta={
              kpi.meter != null
                ? <Meter value={kpi.meter} tone="mint" />
                : kpi.meta
            }
          />
        </div>
      ))}
    </div>
  );
}

/** Shared frame: heading, the sample-data badge, hero band and KPI row. */
function DashboardFrame({
  title, subtitle, actions, data, isLoading, error, refetch,
  hero, kpiCols, children,
}) {
  /* Every dashboard is a hero band, a KPI row and two columns of cards, so the
     wait can be that arrangement rather than a spinner in the middle of an
     empty page. Nothing moves when the numbers arrive — which matters most here,
     because a dashboard is the screen people land on and immediately click. */
  if (isLoading) return <SkeletonDashboard hero={Boolean(hero)} kpiCols={kpiCols} />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  return (
    <>
      <PageHeader
        title={title}
        subtitle={subtitle}
        badge={isMock(data) ? <MockBadge section="dashboards" /> : null}
      >
        {actions}
      </PageHeader>
      {hero}
      <KpiRow kpis={data?.kpis} cols={kpiCols} />
      {children}
    </>
  );
}

/**
 * The activity stream, in the prototype's `.tl` rail.
 *
 * Shared by the dashboards and the Feed screen, so a change to how an event
 * reads happens once. There is no separate feed endpoint — `docs/API.md` §8
 * says `GET /activities` *is* the activity feed — so this is real
 * `crm.activity` rows, recent-first, narrower than the prototype's
 * cross-module story (no stage-move/won-lost/approval events) but genuinely
 * live. See `features/activities/ActivitiesPage.jsx`'s own feed panel and
 * `feedFormat.jsx`, which format the same data identically.
 */
export function RecentActivityCard({ limit = 6, className = '' }) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.activities.feed({ limit }),
    queryFn: () => listActivities({ sort: '-created_at', limit }),
  });

  return (
    <Card
      title="Recent activity"
      className={className}
      actions={<Link to="/feed" className="t-xs fw-650">View feed</Link>}
    >
      {isLoading ? (
        <SkeletonText lines={6} />
      ) : error ? (
        <ErrorState error={error} onRetry={refetch} />
      ) : (
        <div className="tl">
          {(data?.data ?? []).slice(0, limit).map((item) => {
            const [icon, color] = KIND_META[item.kind] ?? ['bi-circle', 'var(--ink-3)'];
            const { verb } = feedVerb(item);
            const related = relatedRecordLinks(item.related);
            const when = item.completed_at ?? item.created_at;
            return (
              <div key={item.id} className="tl-item">
                <div className="tl-dot" style={{ background: color }}>
                  <i className={`bi ${icon}`} aria-hidden="true" />
                </div>
                <div className="tl-title">
                  <span className="fw-650">{item.owner?.full_name ?? 'Someone'}</span> {verb}{' '}
                  <span className="fw-650">{KIND_LABEL[item.kind] ?? item.kind}: {item.subject}</span>
                </div>
                <div className="tl-meta">
                  {related.length ? related.map((r) => r.text).join(', ') : 'No related record'} · {relativeTime(when)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

/** The white pill the hero bands use on their translucent background. */
const HERO_CHIP = {
  background: 'rgba(255,255,255,.16)',
  color: '#fff',
  borderColor: 'rgba(255,255,255,.24)',
};
const HERO_INK = { color: 'rgba(255,255,255,.82)' };

/* ========================================================================== */
/* Rep                                                                        */
/* ========================================================================== */

/**
 * How overdue a follow-up is.
 *
 * Derived from `due` rather than read from `overdueDays`, because the latter is
 * a number somebody computed at some past moment: the fixture carries
 * `overdueDays: 0` next to a `due` of yesterday, so the row rendered a calm blue
 * badge reading "Overdue 1d". A due date keeps its meaning as the clock moves;
 * a day count does not. `overdueDays` is the fallback for a record that has no
 * date at all.
 */
/** `modules/followups` computes this server-side already (see `days_overdue` in the response) — no need to redo the date math here. */
function overdueDays(followUp) {
  return followUp.days_overdue ?? 0;
}

/** A follow-up's "who"/"related to" — lead, or failing that whichever of opportunity/account it's against. */
function followUpWho(fu) {
  return fu.related?.lead?.full_name ?? fu.related?.opportunity?.name ?? fu.related?.account?.name ?? 'Unknown';
}
function followUpRelated(fu) {
  return fu.related?.account?.name ?? fu.related?.opportunity?.name ?? '—';
}

/** The greeting band: who you are, what is waiting, and how far through quota. */
function RepHero({ name, hero, followUps }) {
  const overdue = followUps.filter((f) => overdueDays(f) > 0).length;
  const gap = hero ? Math.max(0, hero.target - hero.attained) : 0;

  return (
    <div className="hero-card mb-3 fade-up">
      <div className="row align-items-center g-3">
        <div className="col-lg-7">
          <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
            <span className="pill" style={HERO_CHIP}>
              <i className="bi bi-sun-fill" aria-hidden="true" />
              {new Date().toLocaleDateString(undefined, { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
            {hero?.territory && (
              <span className="pill" style={HERO_CHIP}>
                <i className="bi bi-geo-alt-fill" aria-hidden="true" />{hero.territory}
              </span>
            )}
          </div>
          <h1 className="text-white fw-750 mb-1" style={{ fontSize: 'var(--fs-2xl)', letterSpacing: '-.035em' }}>
            {greeting()}, {name}
          </h1>
          <p className="mb-3 t-sm" style={HERO_INK}>
            {followUps.length > 0 ? (
              <>
                You have <strong className="text-white">{followUps.length} follow-ups</strong> outstanding
                {overdue > 0 && <>, <strong className="text-white">{overdue} overdue</strong></>}.
              </>
            ) : (
              <>Nothing is overdue. Every follow-up you own is on schedule.</>
            )}
            {hero?.closestWin && (
              <>
                {' '}Your closest win is{' '}
                <strong className="text-white">
                  {hero.closestWin.name} — {moneyShort(hero.closestWin.amount, hero.currency)}
                </strong>.
              </>
            )}
          </p>
          <div className="d-flex flex-wrap gap-2">
            <Link to="/activities?view=followups" className="btn btn-sm-x" style={{ background: '#fff', color: 'var(--brand-700)', fontWeight: 650 }}>
              <i className="bi bi-lightning-charge-fill me-1" aria-hidden="true" />Start my day
            </Link>
            <Link to="/leads" className="btn btn-sm-x" style={{ background: 'rgba(255,255,255,.16)', color: '#fff', border: '1px solid rgba(255,255,255,.28)' }}>
              <i className="bi bi-magnet me-1" aria-hidden="true" />
              {hero?.newLeads ? `${hero.newLeads} new leads` : 'My leads'}
            </Link>
          </div>
        </div>

        {hero && (
          <div className="col-lg-5">
            <div className="p-3" style={{ background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.2)', borderRadius: 'var(--r-lg)' }}>
              <div className="d-flex justify-content-between align-items-baseline mb-1">
                <span className="t-2xs tracking-wide text-uppercase" style={{ color: 'rgba(255,255,255,.7)' }}>
                  Quota attainment
                </span>
                <span className="t-lg fw-750 text-white">{hero.pct}%</span>
              </div>
              <div className="bar thick mb-2" style={{ background: 'rgba(255,255,255,.2)' }}>
                <span style={{ width: `${hero.pct}%`, background: '#fff' }} />
              </div>
              <div className="d-flex justify-content-between t-xs" style={{ color: 'rgba(255,255,255,.8)' }}>
                <span>Achieved <strong className="text-white">{money(hero.attained, hero.currency)}</strong></span>
                <span>Target <strong className="text-white">{money(hero.target, hero.currency)}</strong></span>
              </div>
              <hr className="my-2" style={{ borderColor: 'rgba(255,255,255,.18)', opacity: 1 }} />
              <div className="d-flex justify-content-between t-xs" style={{ color: 'rgba(255,255,255,.8)' }}>
                <span><i className="bi bi-calendar-check me-1" aria-hidden="true" />{hero.daysLeft} days remaining</span>
                <span>Gap <strong className="text-white">{money(gap, hero.currency)}</strong></span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** The header row, shared by the live table and its skeleton so both measure the same. */
const PRIORITY_COLUMNS = [
  { key: 'who', header: 'Who' },
  { key: 'related', header: 'Related to' },
  { key: 'channel', header: 'Channel' },
  { key: 'due', header: 'Due' },
  { key: 'action', header: 'Action' },
];

/** Today's work, oldest debt first. */
function PrioritiesCard({ query }) {
  const rows = query.data?.data ?? [];
  const sorted = [...rows].sort((a, b) => overdueDays(b) - overdueDays(a)).slice(0, 8);

  return (
    <Card
      className="mb-3 fade-up d-3"
      title="Today's priorities"
      subtitle="Oldest debt first — the follow-ups nobody has answered"
      actions={<Link to="/activities" className="btn btn-soft btn-sm-x">Open planner</Link>}
      bare
      footer={
        rows.length > 0 && (
          <>
            <span className="t-xs ink-3">Showing {sorted.length} of {rows.length} outstanding</span>
            <Link to="/activities?view=followups" className="ms-auto t-xs fw-650">
              View all <i className="bi bi-arrow-right" aria-hidden="true" />
            </Link>
          </>
        )
      }
    >
      {query.isLoading ? (
        <SkeletonTable rows={5} columns={PRIORITY_COLUMNS} />
      ) : rows.length === 0 ? (
        <div className="card-body-x">
          <EmptyState icon="bi-check2-circle" title="Nothing overdue" message="Every follow-up is on schedule." />
        </div>
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Who</th>
                <th>Related to</th>
                <th>Channel</th>
                <th>Due</th>
                <th className="text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((fu) => (
                <tr key={fu.id}>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <Avatar name={followUpWho(fu)} size={27} />
                      <div className="min-w-0">
                        <div className="strong text-truncate">{followUpWho(fu)}</div>
                        <div className="t-2xs ink-4 text-truncate">{fu.note}</div>
                      </div>
                    </div>
                  </td>
                  <td>{followUpRelated(fu)}</td>
                  <td><Badge tone="neutral">{fu.channel ?? '—'}</Badge></td>
                  <td>
                    <Badge tone={overdueDays(fu) > 0 ? 'rose' : 'sky'}>
                      <i className="bi bi-clock-fill" aria-hidden="true" />
                      {dueLabel(fu.due_at)}
                    </Badge>
                  </td>
                  <td className="text-end">
                    <div className="row-actions">
                      <Link to="/activities?view=followups" className="btn btn-soft btn-sm-x">Open</Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/** `.score` bands are the prototype's, and the thresholds match `md.score_band`. */
const scoreClass = (score) => (score >= 80 ? 'score-hot' : score >= 50 ? 'score-warm' : 'score-cold');

function HotLeadsCard({ leads = [] }) {
  return (
    <Card
      className="fade-up d-4"
      title="Hot leads to work"
      subtitle="Score ≥ 80 · assigned to me · no activity in 48h"
      actions={<Link to="/leads" className="btn btn-soft btn-sm-x">All leads</Link>}
      bare
    >
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Lead</th><th>Company</th><th>Source</th><th>Score</th>
              <th>Status</th><th className="num">Est. value</th><th>Next follow-up</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((lead) => (
              <tr key={lead.id}>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <Avatar name={lead.name} size={27} />
                    <div className="min-w-0">
                      <Link to={`/leads/${lead.id}`} className="strong d-block text-truncate">{lead.name}</Link>
                      <div className="t-2xs ink-4 text-truncate">{lead.title}</div>
                    </div>
                  </div>
                </td>
                <td>{lead.company}</td>
                <td><Badge tone="neutral">{lead.source}</Badge></td>
                <td><span className={`score ${scoreClass(lead.score)}`}>{lead.score}</span></td>
                <td><Badge tone={lead.tone}>{lead.status}</Badge></td>
                <td className="num strong">{moneyShort(lead.value)}</td>
                <td>
                  {lead.overdue
                    ? <Badge tone="rose">{lead.next}</Badge>
                    : <span className="t-xs ink-2">{lead.next}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** Four counts and a completion bar — the whole follow-up position at a glance. */
function FollowUpSnapshotCard({ snapshot }) {
  if (!snapshot) return null;
  const tiles = [
    { value: snapshot.dueToday, label: 'Due today', color: 'var(--brand-600)', bg: 'var(--surface-2)', border: 'var(--line-2)' },
    { value: snapshot.overdue, label: 'Overdue', color: 'var(--rose)', bg: '#fff1f2', border: '#fcd9de' },
    { value: snapshot.upcoming, label: 'Upcoming', color: 'var(--ink)', bg: 'var(--surface-2)', border: 'var(--line-2)' },
    { value: snapshot.completed, label: 'Completed', color: 'var(--emerald)', bg: '#ecfdf5', border: '#c9f0e0' },
  ];

  return (
    <Card className="mb-3 fade-up d-2" title="Follow-up snapshot" actions={<Badge tone="brand">Live</Badge>}>
      <div className="row g-2 text-center">
        {tiles.map((t) => (
          <div className="col-6" key={t.label}>
            <div className="p-3" style={{ background: t.bg, borderRadius: 'var(--r)', border: `1px solid ${t.border}` }}>
              <div className="t-2xl fw-750 lh-1 tabular" style={{ color: t.color }}>{t.value}</div>
              <div className="t-2xs ink-3 mt-1">{t.label}</div>
            </div>
          </div>
        ))}
      </div>
      <hr className="soft" />
      <div className="d-flex align-items-center justify-content-between mb-1">
        <span className="t-xs ink-3">Completion rate this week</span>
        <span className="t-xs fw-700">{snapshot.completionRate}%</span>
      </div>
      <Meter value={snapshot.completionRate} tone="mint" />
    </Card>
  );
}

/**
 * Suggestions the assistant would make.
 *
 * Nothing generates these — §13 automation is unbuilt and there is no model
 * behind them — so the card says so in its own footnote rather than implying a
 * live analysis. The Dismiss control is omitted for the same reason the
 * approve/reject buttons are: there is nowhere to record the dismissal, and a
 * suggestion that reappears on every refresh teaches people to ignore the card.
 */
function AssistantCard({ suggestions = [] }) {
  if (suggestions.length === 0) return null;

  return (
    <Card
      className="mb-3 fade-up d-3"
      title="AI sales assistant"
      subtitle="Suggestions you can act on"
      actions={<Badge tone="violet"><i className="bi bi-stars" aria-hidden="true" />Preview</Badge>}
      bodyClass="d-flex flex-column gap-2"
    >
      {suggestions.map((s) => (
        <div key={s.id} className="p-2 px-3" style={{ background: 'var(--surface-2)', border: '1px solid var(--line-2)', borderRadius: 'var(--r)' }}>
          <div className="d-flex align-items-center gap-2 mb-1">
            <Badge tone={s.tone}><i className={`bi ${s.icon}`} aria-hidden="true" />{s.badge}</Badge>
          </div>
          <div className="t-sm fw-600">{s.title}</div>
          <div className="t-xs ink-3 mt-1">{s.detail}</div>
        </div>
      ))}
      <div className="t-2xs ink-4 d-flex gap-1 align-items-start">
        <i className="bi bi-info-circle mt-1" aria-hidden="true" />
        <span>
          Sample suggestions. No model produces these yet — automation (§13) is not built, and
          nothing here writes to a CRM record.
        </span>
      </div>
    </Card>
  );
}

/** Birthdays and anniversaries inside 30 days — a reason to call that isn't a pitch. */
function AnniversariesCard() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.contacts.occasions(30),
    queryFn: () => listOccasions({ within_days: 30 }),
  });
  const rows = data?.data ?? [];

  const whenLabel = (days) => (days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `in ${days}d`);
  const urgency = (days) => (days <= 1 ? 'rose' : days <= 7 ? 'amber' : 'neutral');

  return (
    <Card
      className="mb-3 fade-up d-4"
      title="Upcoming anniversaries"
      subtitle="A reason to reach out that isn't a pitch"
      actions={<Badge tone="rose">{rows.length}</Badge>}
    >
      {isLoading ? (
        <SkeletonText lines={4} />
      ) : error ? (
        /* Not an empty list. "Nothing in the next 30 days" is a claim about the
           data, and rendering it when the request failed states something
           untrue — which is how a wrong request path stayed invisible here. */
        <ErrorState error={error} onRetry={refetch} />
      ) : rows.length === 0 ? (
        <div className="t-xs ink-3 text-center py-2">Nothing in the next 30 days.</div>
      ) : (
        <>
          {rows.slice(0, 4).map((o) => (
            <div
              key={`${o.contact.id}-${o.kind}`}
              className="d-flex align-items-center gap-2 p-2 mb-1"
              style={{ border: '1px solid var(--line)', borderRadius: 'var(--r)' }}
            >
              <Avatar name={o.contact.full_name} size={27} />
              <div className="min-w-0 flex-grow-1">
                <div className="t-sm fw-600 text-truncate">{o.contact.full_name}</div>
                <div className="t-2xs ink-4 text-truncate">
                  {o.label}{o.years ? ` · ${o.years} yr${o.years === 1 ? '' : 's'}` : ''}
                  {o.contact.account ? ` · ${o.contact.account.name}` : ''}
                </div>
              </div>
              <Badge tone={urgency(o.days_until)} className="no-wrap">{whenLabel(o.days_until)}</Badge>
            </div>
          ))}
          <Link to="/contacts" className="btn btn-soft btn-sm-x w-100 mt-2">
            View all anniversaries <i className="bi bi-arrow-right ms-1" aria-hidden="true" />
          </Link>
        </>
      )}
    </Card>
  );
}

function ActivityWeekCard({ trend = [], totals }) {
  return (
    <Card className="fade-up d-5" title="My activity this week">
      <BarChart
        data={trend}
        xKey="label"
        series={[
          { key: 'calls', label: 'Calls' },
          { key: 'emails', label: 'Emails' },
          { key: 'meetings', label: 'Meetings' },
        ]}
        stacked
        height={180}
      />
      {totals && (
        <>
          <hr className="soft" />
          <div className="row g-2 text-center t-xs">
            {[['Calls', totals.calls], ['Meetings', totals.meetings], ['Emails', totals.emails]].map(([label, value]) => (
              <div className="col-4" key={label}>
                <div className="fw-750 t-lg tabular">{value}</div>
                <div className="ink-3">{label}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

/**
 * The individual contributor's day: what is due, what is in flight, how far
 * through quota. Ordered by what a rep opens the app to answer — "what do I
 * have to do today" — so the follow-up list sits above the charts.
 */
export function RepDashboard() {
  const { user } = useAuth();
  const dash = useQuery({ queryKey: queryKeys.dashboards('rep'), queryFn: () => getDashboard('rep') });
  const followUps = useQuery({ queryKey: queryKeys.followUps.list({}), queryFn: () => listFollowUps() });
  const d = dash.data;

  return (
    <DashboardFrame
      title="My dashboard"
      subtitle="Your pipeline, your activities, your quota."
      data={d}
      isLoading={dash.isLoading}
      error={dash.error}
      refetch={dash.refetch}
      hero={
        <RepHero
          name={user?.full_name?.split(' ')[0] ?? 'there'}
          hero={d?.hero}
          followUps={followUps.data?.data ?? []}
        />
      }
    >
      <div className="row g-3 mb-3">
        <div className="col-lg-6 col-xl-4"><TodayActivitiesCard today={d?.todayActivities} className="h-100 fade-up d-2" /></div>
        <div className="col-lg-6 col-xl-4"><PipelineFlowCard stages={d?.pipelineCounts} className="h-100 fade-up d-2" /></div>
        <div className="col-xl-4">
          <MySalesCard sales={d?.mySales} conversion={d?.conversion} expected={d?.expectedRevenue} className="h-100 fade-up d-3" />
        </div>
      </div>

      <div className="row g-3">
        <div className="col-xl-8">
          <UpcomingTodayCard items={d?.upcomingToday} className="mb-3 fade-up d-2" />

          <Card
            className="mb-3 fade-up d-2"
            title="My pipeline by stage"
            subtitle="Open opportunity value across the New Business pipeline"
          >
            <BarChart
              data={d?.stageMix ?? []}
              xKey="label"
              series={[{ key: 'value', label: 'Value' }]}
              money
              height={236}
            />
          </Card>

          <MyOpportunitiesCard opps={d?.myOpportunities} className="mb-3 fade-up d-3" />
          <PrioritiesCard query={followUps} />
          <HotLeadsCard leads={d?.hotLeads} />
          <RecentQuotationsCard quotes={d?.recentQuotations} className="mt-3 fade-up d-5" />
        </div>

        <div className="col-xl-4">
          <PendingApprovalsCard approvals={d?.pendingApprovals} className="mb-3 fade-up d-2" />
          <OverdueActivitiesCard overdue={d?.overdueActivities} className="mb-3 fade-up d-2" />
          <FollowUpSnapshotCard snapshot={d?.followUpSnapshot} />
          <NewLeadsCard leads={d?.newLeads} className="mb-3 fade-up d-3" />
          <AssistantCard suggestions={d?.assistant} />
          <RecentActivityCard className="mb-3 fade-up d-4" limit={5} />
          <RecentCustomersCard customers={d?.recentCustomers} className="mb-3 fade-up d-4" />
          <AnniversariesCard />
          <DailySummaryCard summary={d?.dailySummary} className="mb-3 fade-up d-5" />
          <ActivityWeekCard trend={d?.activityTrend} totals={d?.activityTotals} />
        </div>
      </div>
    </DashboardFrame>
  );
}

/* ========================================================================== */
/* Manager                                                                    */
/* ========================================================================== */

/** The amber strip that opens the manager's day: what is blocking other people. */
function ApprovalStrip({ alert }) {
  if (!alert) return null;
  const breached = alert.oldestHours >= alert.slaHours;

  return (
    <section
      className="card-x mb-3 fade-up"
      style={{ borderColor: '#fbe6b4', background: 'linear-gradient(100deg,#fffbeb,#fff 55%)' }}
    >
      <div className="card-body-x d-flex flex-wrap align-items-center gap-3">
        <div className="stat-ico" style={{ background: 'var(--grad-warm)' }}>
          <i className="bi bi-clipboard-check-fill" aria-hidden="true" />
        </div>
        <div className="flex-grow-1 min-w-0">
          <div className="fw-650 t-md">
            {alert.count} approval{alert.count === 1 ? ' is' : 's are'} waiting on you
          </div>
          <div className="t-xs ink-3">
            {alert.detail} Oldest waiting {alert.oldestHours} hours — SLA is {alert.slaHours}h
            {breached && <strong className="ms-1" style={{ color: 'var(--rose)' }}>· breached</strong>}.
          </div>
        </div>
        <Link to="/quotations" className="btn btn-brand btn-sm-x no-wrap">
          Review queue <i className="bi bi-arrow-right ms-1" aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
}

/** Stage-to-stage conversion, each bar relative to the top of the funnel. */
const FUNNEL_COLOURS = ['#6366f1', '#7c6cf0', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899'];

function FunnelCard({ funnel = [], stats }) {
  const top = funnel[0]?.value ?? 1;

  return (
    <Card className="h-100 fade-up d-3" title="Conversion funnel" actions={<Badge tone="brand">Q3</Badge>}>
      <div className="d-flex flex-column gap-2">
        {funnel.map((stage, i) => (
          <div key={stage.label}>
            <div className="d-flex justify-content-between align-items-baseline t-xs mb-1">
              <span className="fw-600 ink-2">{stage.label}</span>
              <span className="fw-750 tabular">
                {number(stage.value)}
                {i > 0 && (
                  <span className="t-2xs ink-4 ms-1">
                    {Math.round((stage.value / funnel[i - 1].value) * 100)}%
                  </span>
                )}
              </span>
            </div>
            <div className="bar thick">
              <span style={{ width: `${(stage.value / top) * 100}%`, background: FUNNEL_COLOURS[i % FUNNEL_COLOURS.length] }} />
            </div>
          </div>
        ))}
      </div>
      {stats && (
        <>
          <hr className="soft" />
          <div className="d-flex justify-content-between t-xs">
            <span className="ink-3">Lead → Won conversion</span><span className="fw-750">{stats.leadToWon}%</span>
          </div>
          <div className="d-flex justify-content-between t-xs mt-1">
            <span className="ink-3">Avg. lead response time</span><span className="fw-750">{stats.responseMinutes} min</span>
          </div>
        </>
      )}
    </Card>
  );
}

function RepPerformanceCard({ reps = [] }) {
  /* A rep with no quota set has no attainment — 0/0 is NaN and x/0 is Infinity. */
  const attain = (r) => (Number(r.quota) > 0 ? Math.round((Number(r.achieved || 0) / Number(r.quota)) * 100) : null);
  const totalQuota = reps.reduce((s, r) => s + (Number(r.quota) || 0), 0);
  const totalAchieved = reps.reduce((s, r) => s + (Number(r.achieved) || 0), 0);
  const teamPct = totalQuota ? Math.round((totalAchieved / totalQuota) * 100) : 0;

  return (
    <Card
      className="fade-up d-3"
      title="Sales representative performance"
      subtitle="Quarter to date · sorted by attainment"
      actions={<Link to="/targets" className="btn btn-soft btn-sm-x">Targets &amp; quotas</Link>}
      bare
      footer={
        <>
          <span className="t-xs ink-3">{reps.length} representatives · team attainment {teamPct}%</span>
          <Link to="/reports" className="ms-auto t-xs fw-650">
            Full performance report <i className="bi bi-arrow-right" aria-hidden="true" />
          </Link>
        </>
      }
    >
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Representative</th>
              <th className="num">Quota</th>
              <th className="num">Achieved</th>
              <th style={{ width: 170 }}>Attainment</th>
              <th className="num">Pipeline</th>
              <th className="num">Win %</th>
              <th>Activities</th>
            </tr>
          </thead>
          <tbody>
            {[...reps].sort((a, b) => (attain(b) ?? -1) - (attain(a) ?? -1)).map((rep) => {
              const pct = attain(rep);
              return (
                <tr key={rep.name}>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <Avatar name={rep.name} size={27} />
                      <div className="min-w-0">
                        <div className="strong text-truncate">{rep.name}</div>
                        <div className="t-2xs ink-4 text-truncate">{rep.territory}</div>
                      </div>
                    </div>
                  </td>
                  <td className="num">{moneyShort(rep.quota || 0)}</td>
                  <td className="num strong">{moneyShort(rep.achieved || 0)}</td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      {pct == null ? (
                        <span className="t-xs ink-4">No quota set</span>
                      ) : (
                        <>
                          <Meter value={pct} tone={pct >= 90 ? 'mint' : pct >= 65 ? 'brand' : 'warm'} />
                          <Badge tone={pct >= 90 ? 'emerald' : pct >= 65 ? 'amber' : 'rose'}>{pct}%</Badge>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="num">{moneyShort(rep.pipeline || 0)}</td>
                  <td className="num">{Number.isFinite(Number(rep.winRate)) && rep.winRate != null ? `${rep.winRate}%` : '—'}</td>
                  <td className="t-xs ink-3 no-wrap">{rep.activities}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/**
 * What is waiting on a decision.
 *
 * Approving from here would need §7's decision endpoint and a reason — the
 * quotation screen already owns both, so each row opens it rather than
 * duplicating a decision that has to be audited.
 */
function ApprovalQueueCard({ approvals = [], total, rule }) {
  /* The pill counts the queue, not the rows on screen. Reading `approvals.length`
     made it disagree with the strip at the top of the page — "6 waiting on you"
     above "3 pending" — and the smaller number is the one that gets believed. */
  const pending = total ?? approvals.length;

  return (
    <Card
      className="mb-3 fade-up d-4"
      title="Approval queue"
      subtitle={pending > approvals.length ? `Oldest ${approvals.length} of ${pending}` : null}
      actions={<Badge tone="rose">{pending} pending</Badge>}
      bodyClass="d-flex flex-column gap-2"
    >
      {approvals.map((a) => (
        <div
          key={a.id}
          className="d-flex align-items-center gap-2 p-2 px-3"
          style={{ border: '1px solid var(--line)', borderRadius: 'var(--r)' }}
        >
          <Avatar name={a.requester} size={27} />
          <div className="flex-grow-1 min-w-0">
            <div className="t-sm fw-600 text-truncate">{a.title}</div>
            <div className="t-2xs ink-4 text-truncate">
              {a.id} · {moneyShort(a.amount)} · {a.requester} · {a.waited}
            </div>
          </div>
          <Link to="/quotations" className="btn btn-soft btn-sm-x no-wrap">Review</Link>
        </div>
      ))}
      {rule && (
        <div className="t-2xs ink-4 d-flex align-items-start gap-1">
          <i className="bi bi-diagram-2 mt-1" aria-hidden="true" />
          <span>{rule}</span>
        </div>
      )}
      <Link to="/quotations" className="btn btn-soft btn-sm-x w-100">
        Open all {pending} approvals
      </Link>
    </Card>
  );
}

const DOT_COLOUR = {
  rose: 'var(--rose)', amber: 'var(--amber)', emerald: 'var(--emerald)',
  sky: 'var(--sky)', violet: 'var(--violet)', brand: 'var(--brand-500)',
};

function CoachingCard({ signals = [] }) {
  return (
    <Card
      className="fade-up d-6"
      title="Coaching signals"
      actions={<Badge tone="violet"><i className="bi bi-stars" aria-hidden="true" />Preview</Badge>}
      bodyClass="d-flex flex-column gap-2 t-sm"
    >
      {signals.map((s) => (
        <div className="d-flex gap-2" key={s.name}>
          <span className="dot-ind mt-2" style={{ background: DOT_COLOUR[s.tone] ?? 'var(--brand-500)' }} />
          <div><span className="fw-650">{s.name}</span> — {s.note}</div>
        </div>
      ))}
    </Card>
  );
}

/**
 * The team view. A manager's question is "who needs help and what is slipping",
 * so attainment is a bar rather than a number — a row that is visibly short is
 * faster to spot than one reading 49%.
 */
export function ManagerDashboard() {
  const dash = useQuery({ queryKey: queryKeys.dashboards('manager'), queryFn: () => getDashboard('manager') });
  const d = dash.data;

  return (
    <DashboardFrame
      title={d?.team?.name ?? 'Team dashboard'}
      subtitle={d?.team ? `${d.team.reps} reps · ${d.team.period}` : 'Attainment, pipeline health and approvals waiting on you.'}
      data={d}
      isLoading={dash.isLoading}
      error={dash.error}
      refetch={dash.refetch}
      kpiCols="col-6 col-xl-3"
      actions={
        <>
          <Link to="/targets" className="btn btn-soft btn-sm-x">
            <i className="bi bi-bullseye me-1" aria-hidden="true" />Targets
          </Link>
          <Link to="/leads" className="btn btn-brand btn-sm-x">
            <i className="bi bi-shuffle me-1" aria-hidden="true" />Assign leads
          </Link>
        </>
      }
      hero={<ApprovalStrip alert={d?.approvalAlert} />}
    >
      <div className="row g-3 mb-3">
        <div className="col-xl-8">
          <Card
            className="h-100 fade-up d-2"
            title="Revenue vs target &amp; forecast"
            subtitle="Closed-won, commit and best-case by month"
          >
            <ComboChart
              data={d?.revenueTrend ?? []}
              xKey="label"
              height={272}
              series={[
                { key: 'closed', label: 'Closed won', type: 'bar', color: '#6366f1', stack: 's' },
                { key: 'commit', label: 'Commit', type: 'bar', color: '#a5b4fc', stack: 's' },
                { key: 'bestCase', label: 'Best case', type: 'bar', color: '#e0e7ff', stack: 's' },
                { key: 'target', label: 'Target', type: 'line', color: '#f43f5e', dash: true },
              ]}
            />
          </Card>
        </div>
        <div className="col-xl-4">
          <FunnelCard funnel={d?.funnel} stats={d?.funnelStats} />
        </div>
      </div>

      <div className="row g-3">
        <div className="col-xl-7">
          <RepPerformanceCard reps={d?.reps} />
        </div>
        <div className="col-xl-5">
          <ApprovalQueueCard approvals={d?.approvals} total={d?.approvalCount} rule={d?.approvalRule} />
          <Card className="mb-3 fade-up d-5" title="Lead distribution by source">
            <DonutChart data={d?.sourceMix ?? []} height={210} />
          </Card>
          <CoachingCard signals={d?.coaching} />
        </div>
      </div>
    </DashboardFrame>
  );
}

/* ========================================================================== */
/* Executive                                                                  */
/* ========================================================================== */

/** Closed revenue against plan — the number an executive opens the page for. */
function ExecHero({ hero }) {
  if (!hero) return null;
  const chip = { background: 'rgba(255,255,255,.2)', color: '#fff', borderColor: 'rgba(255,255,255,.28)' };

  return (
    <div className="hero-card h-100 fade-up">
      <div className="eyebrow mb-2" style={{ color: 'rgba(255,255,255,.72)' }}>Closed revenue · quarter to date</div>
      <div className="fw-750 text-white lh-1" style={{ fontSize: 'var(--fs-4xl)', letterSpacing: '-.04em' }}>
        {moneyShort(hero.closed, hero.currency)}
      </div>
      <div className="d-flex align-items-center gap-2 mt-2 mb-3 flex-wrap">
        <span className="pill" style={chip}>
          <i className="bi bi-arrow-up-short" aria-hidden="true" /> {hero.yoy}% YoY
        </span>
        <span className="t-xs" style={{ color: 'rgba(255,255,255,.75)' }}>
          vs {moneyShort(hero.priorYear, hero.currency)} a year ago
        </span>
      </div>
      <div className="bar thick mb-2" style={{ background: 'rgba(255,255,255,.2)' }}>
        <span style={{ width: `${hero.pct}%`, background: '#fff' }} />
      </div>
      <div className="d-flex justify-content-between t-xs" style={{ color: 'rgba(255,255,255,.82)' }}>
        <span>{hero.pct}% of the {moneyShort(hero.plan, hero.currency)} quarterly plan</span>
        <span>{hero.daysLeft} days left</span>
      </div>
    </div>
  );
}

/** The indigo ramp: closed → pipeline is an ordering, not six unrelated categories. */
const FORECAST_COLOURS = ['#6366f1', '#8b5cf6', '#c4b5fd', '#e0e7ff'];
const HEALTH_COLOURS = ['#10b981', '#0ea5e9', '#f59e0b', '#f43f5e'];

function ForecastCard({ categories = [], projected }) {
  return (
    <Card className="h-100 fade-up d-3" title="Forecast categories" actions={<Badge tone="brand">Q3 close</Badge>}>
      <DonutChart data={categories} height={180} colors={FORECAST_COLOURS} legend={false} />
      <div className="d-flex flex-column gap-2 t-sm mt-3">
        {categories.map((c, i) => (
          <div className="d-flex justify-content-between" key={c.label}>
            <span>
              <span className="dot-ind me-2" style={{ background: FORECAST_COLOURS[i % FORECAST_COLOURS.length] }} />
              {c.label}
            </span>
            <span className="fw-750 tabular">{moneyShort(c.value)}</span>
          </div>
        ))}
        {projected && (
          <>
            <hr className="soft my-1" />
            <div className="d-flex justify-content-between">
              <span className="fw-650">Projected close</span>
              <span className="fw-750 tabular" style={{ color: 'var(--brand-600)' }}>{moneyShort(projected.amount)}</span>
            </div>
            <div className="t-2xs ink-4">{projected.planPct}% of plan · {projected.basis}</div>
          </>
        )}
      </div>
    </Card>
  );
}

const REGION_COLOURS = ['#6366f1', '#8b5cf6', '#0ea5e9', '#10b981'];

function RegionsCard({ regions = [] }) {
  return (
    <Card className="h-100 fade-up d-3" title="Regional performance">
      <div className="d-flex flex-column gap-3">
        {regions.map((r, i) => {
          const pct = Math.round((r.actual / r.target) * 100);
          return (
            <div key={r.label}>
              <div className="d-flex justify-content-between align-items-baseline mb-1">
                <span className="t-sm fw-600">{r.label}</span>
                <span className="t-xs ink-3">
                  {moneyShort(r.actual)} <span className="ink-4">/ {moneyShort(r.target)}</span>
                  <Badge tone={pct >= 95 ? 'emerald' : pct >= 80 ? 'amber' : 'rose'} className="ms-1">{pct}%</Badge>
                </span>
              </div>
              <div className="bar thick">
                <span style={{ width: `${Math.min(pct, 100)}%`, background: REGION_COLOURS[i % REGION_COLOURS.length] }} />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function HealthCard({ health = [], accounts }) {
  return (
    <Card
      className="h-100 fade-up d-5"
      title="Customer health mix"
      actions={accounts ? <span className="t-2xs ink-4">{number(accounts)} accounts</span> : null}
    >
      <DonutChart data={health} height={160} colors={HEALTH_COLOURS} legend={false} />
      <div className="row g-2 t-xs text-center mt-2">
        {health.map((h, i) => (
          <div className="col-3" key={h.label}>
            <div className="fw-750 t-md" style={{ color: HEALTH_COLOURS[i % HEALTH_COLOURS.length] }}>{h.value}%</div>
            <div className="ink-3">{h.label}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function TopDealsCard({ deals = [] }) {
  return (
    <Card
      className="fade-up d-4"
      title="Largest open opportunities"
      actions={<Link to="/opportunities" className="t-xs fw-650">View all</Link>}
      bare
    >
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Opportunity</th><th>Region</th><th>Stage</th>
              <th className="num">Value</th><th className="num">Prob.</th><th>Close</th>
            </tr>
          </thead>
          <tbody>
            {deals.map((deal) => (
              <tr key={deal.id}>
                <td>
                  <div className="strong">{deal.name}</div>
                  <div className="t-2xs ink-4">{deal.sub}</div>
                </td>
                <td>{deal.region}</td>
                <td><Badge tone={deal.tone}>{deal.stage}</Badge></td>
                <td className="num strong">{moneyShort(deal.value)}</td>
                <td className="num">{deal.probability}%</td>
                <td className="t-xs no-wrap">{deal.close}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

const SIGNAL_EDGE = {
  emerald: 'var(--emerald)', rose: 'var(--rose)', amber: 'var(--amber)', sky: 'var(--sky)',
};

function SignalsCard({ signals = [] }) {
  return (
    <Card
      className="h-100 fade-up d-5"
      title="Executive signals"
      actions={<Badge tone="violet"><i className="bi bi-stars" aria-hidden="true" />Preview</Badge>}
      bodyClass="d-flex flex-column gap-2"
    >
      {signals.map((s) => (
        <div
          key={s.title}
          className="p-3"
          style={{
            borderLeft: `3px solid ${SIGNAL_EDGE[s.tone] ?? 'var(--brand-500)'}`,
            background: 'var(--surface-2)',
            borderRadius: '0 var(--r) var(--r) 0',
          }}
        >
          <div className="t-sm fw-650">{s.title}</div>
          <div className="t-xs ink-3 mt-1">{s.detail}</div>
        </div>
      ))}
    </Card>
  );
}

/** Company-level revenue, forecast and health. Read-only: `exec` is an org-scope viewing role. */
export function ExecDashboard() {
  const dash = useQuery({ queryKey: queryKeys.dashboards('exec'), queryFn: () => getDashboard('exec') });
  const d = dash.data;

  if (dash.isLoading) return <SkeletonDashboard kpiCols="col-6 col-md-3" />;
  if (dash.error) return <ErrorState error={dash.error} onRetry={dash.refetch} />;

  return (
    <>
      <PageHeader
        title="Revenue command centre"
        subtitle="Global · read-only executive view"
        badge={isMock(d) ? <MockBadge section="dashboards" /> : null}
      >
        <Link to="/forecast" className="btn btn-soft btn-sm-x">
          <i className="bi bi-graph-up-arrow me-1" aria-hidden="true" />Forecast
        </Link>
        <Link to="/reports" className="btn btn-brand btn-sm-x">
          <i className="bi bi-file-earmark-bar-graph me-1" aria-hidden="true" />Reports
        </Link>
      </PageHeader>

      {/* Headline band: the hero and the four numbers read as one row. */}
      <div className="row g-3 mb-3">
        <div className="col-lg-5"><ExecHero hero={d?.hero} /></div>
        <div className="col-lg-7">
          <KpiRow kpis={d?.kpis} cols="col-6 col-md-3" className="row g-3 h-100" />
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-xl-8">
          <Card
            className="h-100 fade-up d-2"
            title="Revenue trend &amp; forecast accuracy"
            subtitle="Actual vs forecast vs plan, rolling 12 months"
            footer={
              <div className="d-flex flex-wrap gap-4 t-xs">
                {(d?.trendFacts ?? []).map((f) => (
                  <span className="ink-3" key={f.label}>{f.label} <strong className="ink">{f.value}</strong></span>
                ))}
              </div>
            }
          >
            <ComboChart
              data={d?.revenueTrend ?? []}
              xKey="label"
              height={276}
              unit={(v) => `$${v}M`}
              series={[
                { key: 'plan', label: 'Plan', type: 'bar', color: 'rgba(148,163,220,.22)' },
                { key: 'actual', label: 'Actual revenue', type: 'line', color: '#6366f1', fill: true },
                { key: 'forecast', label: 'Forecast', type: 'line', color: '#ec4899', dash: true },
              ]}
            />
          </Card>
        </div>
        <div className="col-xl-4">
          <ForecastCard categories={d?.forecastCategories} projected={d?.projectedClose} />
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-lg-4"><RegionsCard regions={d?.regions} /></div>
        <div className="col-lg-4">
          <Card className="h-100 fade-up d-4" title="Product line contribution">
            <BarChart
              data={d?.byProduct ?? []}
              xKey="label"
              series={[{ key: 'value', label: 'Revenue' }]}
              money
              horizontal
              height={250}
            />
          </Card>
        </div>
        <div className="col-lg-4"><HealthCard health={d?.health} accounts={d?.healthAccounts} /></div>
      </div>

      <div className="row g-3">
        <div className="col-xl-7"><TopDealsCard deals={d?.topDeals} /></div>
        <div className="col-xl-5"><SignalsCard signals={d?.signals} /></div>
      </div>
    </>
  );
}

/* ========================================================================== */
/* Administrator                                                              */
/* ========================================================================== */

function DataQualityCard({ issues = [] }) {
  return (
    <Card
      className="fade-up d-2"
      title="Data quality"
      subtitle="Issues blocking clean reporting, by severity"
      bare
    >
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Issue</th><th>Module</th><th className="num">Records</th>
              <th style={{ width: 140 }}>Share</th><th>Severity</th>
            </tr>
          </thead>
          <tbody>
            {issues.map((row) => (
              <tr key={row.issue}>
                <td className="strong">{row.issue}</td>
                <td><Badge tone="neutral">{row.module}</Badge></td>
                <td className="num strong">{number(row.records)}</td>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    {/* Share is a percentage of the module's records, so the bar
                        is scaled ×10 — a 4.2% duplication rate drawn to a 100%
                        axis is an invisible sliver. */}
                    <Meter value={Math.min(row.share * 10, 100)} tone={row.severity === 'High' ? 'warm' : 'brand'} />
                    <span className="t-2xs ink-3 tabular no-wrap">{row.share}%</span>
                  </div>
                </td>
                <td><Badge tone={row.tone}>{row.severity}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function AdoptionCard({ adoption = [], stats = [] }) {
  return (
    <Card className="h-100 fade-up d-3" title="Licence &amp; adoption">
      <ComboChart
        data={adoption}
        xKey="label"
        height={190}
        unit={(v) => number(v)}
        series={[
          { key: 'active', label: 'Daily active', type: 'line', color: '#6366f1', fill: true },
          { key: 'licensed', label: 'Licensed', type: 'line', color: '#cbd5e1', dash: true },
        ]}
      />
      <div className="d-flex flex-column gap-2 t-sm mt-3">
        {stats.map((s) => (
          <div className="d-flex justify-content-between" key={s.label}>
            <span className="ink-3">{s.label}</span>
            <span className="fw-750" style={s.tone ? { color: `var(--${s.tone === 'emerald' ? 'emerald' : 'rose'})` } : undefined}>
              {s.value}{s.share && <span className="t-2xs ink-4"> ({s.share})</span>}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

/** Which audit actions get which pill. Unlisted actions fall back to neutral. */
const AUDIT_TONE = {
  approve: 'amber', reject: 'rose', update: 'sky', stage_change: 'sky',
  permission_change: 'violet', export: 'rose', login: 'emerald',
  login_failed: 'rose', send: 'brand', delete: 'rose',
};

/** `5 Sep 14:40` — the year and the seconds cost the IP column its width. */
function auditTime(iso) {
  const d = new Date(iso);
  return `${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
}

const AUDIT_COLUMNS = [
  { key: 'when', header: 'When' }, { key: 'user', header: 'User' },
  { key: 'action', header: 'Action' }, { key: 'change', header: 'Change' },
  { key: 'ip', header: 'IP' },
];

function AuditCard() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.admin.audit({ limit: 6 }),
    queryFn: () => listAuditTrail({ limit: 6 }),
  });

  return (
    <Card
      className="fade-up d-4"
      title="Recent audit trail"
      subtitle="Hash-chained and append-only · retained 7 years"
      actions={<Link to="/admin/audit" className="btn btn-soft btn-sm-x">Full log</Link>}
      bare
    >
      {isLoading ? (
        <SkeletonTable rows={6} columns={AUDIT_COLUMNS} />
      ) : error ? (
        <div className="card-body-x"><ErrorState error={error} onRetry={refetch} /></div>
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr><th>When</th><th>User</th><th>Action</th><th>Change</th><th>IP</th></tr>
            </thead>
            <tbody>
              {(data?.data ?? []).map((row) => (
                <tr key={row.id}>
                  <td className="t-xs no-wrap">{auditTime(row.at)}</td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <Avatar name={row.actor} size={22} />
                      <span className="text-truncate">{row.actor}</span>
                    </div>
                  </td>
                  <td><Badge tone={AUDIT_TONE[row.action] ?? 'neutral'}>{row.action.replace(/_/g, ' ')}</Badge></td>
                  <td className="t-xs">
                    <span className="strong">{row.record}</span>
                    <span className="ink-3"> · {row.detail}</span>
                  </td>
                  <td className="t-xs ink-4 no-wrap">{row.ip}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/**
 * Where an administrator goes next.
 *
 * The list is code, not fixture data: these are routes this build has, and a
 * shortcut to a screen that does not exist is a dead end wearing a chevron.
 */
const SHORTCUTS = [
  { to: '/admin/users', icon: 'bi-person-gear', label: 'Users & roles' },
  { to: '/admin/master-data', icon: 'bi-database-gear', label: 'Master data' },
  { to: '/admin/workflows', icon: 'bi-diagram-3', label: 'Workflow builder' },
  { to: '/admin/audit', icon: 'bi-clock-history', label: 'Audit log' },
  { to: '/admin/security', icon: 'bi-shield-check', label: 'Security policy' },
  { to: '/email-templates', icon: 'bi-envelope-paper', label: 'Email templates' },
];

function ShortcutsCard() {
  return (
    <Card className="fade-up d-6" title="Configuration shortcuts">
      <div className="row g-2">
        {SHORTCUTS.map((s) => (
          <div className="col-6" key={s.to}>
            <Link to={s.to} className="shortcut d-flex align-items-center gap-2 p-2 px-3 text-decoration-none">
              <i className={`bi ${s.icon} text-primary`} aria-hidden="true" />
              <span className="t-sm fw-600 ink-2">{s.label}</span>
            </Link>
          </div>
        ))}
      </div>
    </Card>
  );
}

/** Platform health and adoption rather than sales performance. */
export function AdminDashboard() {
  const dash = useQuery({ queryKey: queryKeys.dashboards('admin'), queryFn: () => getDashboard('admin') });
  const d = dash.data;
  const org = d?.organization;

  return (
    <DashboardFrame
      title="System overview"
      subtitle={org ? `${org.name} · ${org.businessUnits} business units · ${org.regions} regions` : 'Adoption, sessions and data quality.'}
      data={d}
      isLoading={dash.isLoading}
      error={dash.error}
      refetch={dash.refetch}
      kpiCols="col-6 col-xl-3"
      actions={
        <Link to="/admin/users" className="btn btn-add btn-sm-x">
          <i className="bi bi-person-plus me-1" aria-hidden="true" />Add user
        </Link>
      }
    >
      <div className="row g-3 mb-3">
        <div className="col-xl-8"><DataQualityCard issues={d?.dataQuality} /></div>
        <div className="col-xl-4"><AdoptionCard adoption={d?.adoption} stats={d?.adoptionStats} /></div>
      </div>

      <div className="row g-3">
        <div className="col-xl-7"><AuditCard /></div>
        <div className="col-xl-5">
          <ShortcutsCard />
        </div>
      </div>
    </DashboardFrame>
  );
}

/* ========================================================================== */
/* Router entry                                                               */
/* ========================================================================== */

/**
 * `/` — render the dashboard the user's role names, without a redirect that
 * would show in history. Falls back to the rep view, which is the least
 * privileged of the four and therefore the safe default.
 */
export function DashboardRouter() {
  const { landingScreen, roleCodes } = useAuth();

  if (landingScreen === 'dashboard.admin' || roleCodes.includes('superadmin')) return <AdminDashboard />;
  if (landingScreen === 'dashboard.exec' || roleCodes.includes('exec')) return <ExecDashboard />;
  if (landingScreen === 'dashboard.manager' || roleCodes.includes('manager')) return <ManagerDashboard />;
  return <RepDashboard />;
}
